import type {
  CommonResponseCollectionSchoolAndRegionResponse,
  CommonResponseSliceSchoolWithRegionResponse,
  DownloadSchoolTestPdfData,
  DownloadSchoolTestPdfParams,
  GetRecentlyDownloadedSchoolsData,
  GetSchoolTestPageWithAllSchoolData,
  GetSchoolTestPageWithAllSchoolParams,
  GetSchoolTestsGroupBySchoolData,
  GetSchoolTestsGroupBySchoolParams,
  Pageable,
  RetrieveAllSchoolParams,
  SchoolTestPdfDownloadLogRequest,
  SchoolTestPdfDownloadStatusResponse,
  SearchSchoolAndRegionParams,
} from '~/api-spec/__generated__/main'
import { httpClient, type RequestConfig, type RequestMeta } from '~/http/client'
import type { WithNullableOptionals } from '~/http/request'
import type { Slice } from '~/schema/slice'

import {
  adaptRecentlyDownloadedSchools,
  adaptSchools,
  adaptSchoolTestFile,
  adaptSchoolTestPdfQuota,
  adaptSchoolTests,
  adaptSchoolTestsBySido,
  adaptSchoolTestSchoolsBySido,
} from './adapters'
import type {
  RecentlyDownloadedSchool,
  School,
  SchoolTest,
  SchoolTestFile,
  SchoolTestPdfQuota,
  SchoolTestsBySido,
  SchoolTestSchoolsBySido,
} from './schemas'

// 서버는 두 조회의 선택 조건을 nullable(`String?`·`Int?`·`Set<…>?`)로 받지만 스펙은 optional 로만 그린다.
// 고등은 학기가 없어 null 을 싣는다. 2026-10-07 백엔드 develop 의 schoolTest/controller/SchoolTestController.kt
// (searchSchoolAndRegion·getSchoolTestsGroupBySchool)로 확인.
export type SchoolTestSchoolsParams = WithNullableOptionals<SearchSchoolAndRegionParams>
export type SchoolTestsGroupBySchoolParams =
  WithNullableOptionals<GetSchoolTestsGroupBySchoolParams>
export type SchoolTestPdfDownloadLogBody = SchoolTestPdfDownloadLogRequest
// 학교 검색 조건도 서버가 nullable 로 받는다. 생성 타입은 Spring Pageable 을 `pageable` 객체로 그리지만 실제 쿼리는
// page·size·sort 를 평탄하게 받는다. 2026-10-13 백엔드 develop(cc5f60e17) 의 schoolTest/controller/SchoolController.kt
// 로 확인.
export type SchoolSearchParams = WithNullableOptionals<Omit<RetrieveAllSchoolParams, 'pageable'>> &
  Pageable

// 기출 회차 목록의 조건도 서버가 nullable 로 받고, 페이지는 page·size 를 평탄하게 받는다. 응답은 CommonResponse 봉투 없이
// Slice 를 바로 낸다. 2026-10-14 백엔드 develop(6a0f8e7bb) 의 schoolTest/controller/SchoolTestController.kt
// (getSchoolTestPageWithAllSchool)로 확인.
export type SchoolTestListParams = WithNullableOptionals<
  Omit<GetSchoolTestPageWithAllSchoolParams, 'pageable'>
> &
  Pageable

export const getSchoolTests = async (
  params: SchoolTestListParams,
  config?: Pick<RequestConfig, 'meta' | 'signal'>,
): Promise<Slice<SchoolTest>> => {
  const body = await httpClient().get<GetSchoolTestPageWithAllSchoolData>('/school-tests/v2/page', {
    params,
    ...config,
  })
  return adaptSchoolTests(body)
}

export const getSchoolTestSchools = async (
  params: SchoolTestSchoolsParams,
  config?: Pick<RequestConfig, 'meta' | 'signal'>,
): Promise<SchoolTestSchoolsBySido[]> => {
  const body = await httpClient().get<CommonResponseCollectionSchoolAndRegionResponse>(
    '/school-tests/schools',
    { params, ...config },
  )
  return adaptSchoolTestSchoolsBySido(body.data) ?? []
}

// 이 엔드포인트와 PDF 한도 조회는 CommonResponse 봉투 없이 본문을 바로 낸다.
export const getSchoolTestsGroupBySchool = async (
  params: SchoolTestsGroupBySchoolParams,
  meta?: RequestMeta,
): Promise<SchoolTestsBySido[]> => {
  const body = await httpClient().get<GetSchoolTestsGroupBySchoolData>(
    '/school-tests/group-by-school',
    { params, meta },
  )
  return adaptSchoolTestsBySido(body) ?? []
}

export const getSchoolTestPdfQuota = async (meta?: RequestMeta): Promise<SchoolTestPdfQuota> => {
  const body = await httpClient().get<SchoolTestPdfDownloadStatusResponse>(
    '/school-tests/pdf-download-status',
    { meta },
  )
  return adaptSchoolTestPdfQuota(body)
}

export const createSchoolTestPdfDownloadLog = async (
  body: SchoolTestPdfDownloadLogBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post('/school-tests/pdf-download-log', body, { meta })
}

export const searchSchools = async (
  params: SchoolSearchParams,
  meta?: RequestMeta,
): Promise<Slice<School>> => {
  const body = await httpClient().get<CommonResponseSliceSchoolWithRegionResponse>('/schools/all', {
    params,
    meta,
  })
  return adaptSchools(body.data)
}

// 두 응답 모두 CommonResponse 봉투 없이 본문을 바로 낸다.
export type SchoolTestFileParams = DownloadSchoolTestPdfParams

/** 고른 기출 회차의 PDF 주소. presignedUrlType 으로 내려받기용·미리보기용을 고른다 */
export const getSchoolTestFile = async (
  params: SchoolTestFileParams,
  meta?: RequestMeta,
): Promise<SchoolTestFile> =>
  adaptSchoolTestFile(
    await httpClient().get<DownloadSchoolTestPdfData>('/school-tests/v2/download', {
      params,
      meta,
    }),
  )

export const getRecentlyDownloadedSchools = async (
  size: number,
  meta?: RequestMeta,
): Promise<RecentlyDownloadedSchool[]> =>
  adaptRecentlyDownloadedSchools(
    await httpClient().get<GetRecentlyDownloadedSchoolsData>(
      '/school-tests/recently-downloaded-schools',
      { params: { size }, meta },
    ),
  )
